import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  IconRss,
  IconSearch,
  IconAdjustmentsHorizontal,
  IconSparkles,
  IconBriefcase,
  IconSend,
  IconStar,
  IconStarFilled,
  IconChevronDown,
  IconX,
  IconWorld,
  IconMapPin,
  IconClock,
  IconPlane,
  IconCalendar,
  IconShare3,
  IconRotate,
  IconFilter,
  IconCheck,
  IconCopy,
  IconBrandTwitter,
  IconBrandFacebook,
  IconBrandWhatsapp,
} from "@tabler/icons-react";
import classes from "./feedRedesign.module.css";
import { currencyFormat } from "../../../utils/functions";
import { errorHandler, toastSuccess } from "../../../utils";
import { ALL_SKILLS } from "../../../constants";
import {
  getTalentJobFeed,
  talentActivitySummary,
  talentGetJobInteraction,
  talentJobInteraction,
  talentRemoveJobInteraction,
} from "../../../services/talentCalls";

const EMPLOYMENT_TYPES = ["Full-Time", "Part-Time", "Internship"];
const WORK_MODES = ["Onsite", "Remote", "Relocation"];

const DATE_POSTED_OPTIONS = [
  { value: "", label: "Anytime" },
  { value: "24h", label: "Last 24 hours" },
  { value: "3d", label: "Last 3 days" },
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
  { value: "30d", label: "Last 30 days" },
];

const emptyDrawerFilters = {
  title: "",
  company: "",
  location: "",
  datePosted: "",
  skills: [],
  min: "",
  max: "",
  types: [],
  modes: [],
  relocationOnly: false,
};

const formatPostedDate = (date) => {
  if (!date) return "—";
  const d = moment(date);
  return d.isSame(moment(), "day") ? "Today" : d.format("MMM D, YYYY");
};

const JobFeed = () => {
  const navigate = useNavigate();

  const [baseJobs, setBaseJobs] = useState([]);
  const [openRolesTotal, setOpenRolesTotal] = useState(0);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedTotal, setSavedTotal] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(emptyDrawerFilters);
  const [skillsPanelOpen, setSkillsPanelOpen] = useState(false);

  const [selectedJob, setSelectedJob] = useState(null);
  const [shareJob, setShareJob] = useState(null);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const first = await getTalentJobFeed(1);
      let records = first?.data?.data?.records || [];
      const total = first?.data?.data?.totalItems || 0;
      setOpenRolesTotal(total);

      // The API paginates server-side (fixed page size) — fetch every
      // remaining page so the feed actually contains every open job,
      // not just the first backend page.
      const backendPageSize = records.length;
      if (backendPageSize > 0 && records.length < total) {
        const totalBackendPages = Math.ceil(total / backendPageSize);
        const remainingPages = Array.from({ length: totalBackendPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(remainingPages.map((p) => getTalentJobFeed(p)));
        rest.forEach((res) => {
          records = records.concat(res?.data?.data?.records || []);
        });
      }

      setBaseJobs(records);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSaved = async () => {
    try {
      const res = await talentGetJobInteraction("star", 1);
      setSavedJobs(res?.data?.data?.data || []);
      setSavedTotal(res?.data?.data?.totalItems || 0);
    } catch (_) {}
  };

  const fetchSummary = async () => {
    try {
      const res = await talentActivitySummary();
      if (res?.data?.data?.metrics) setMetrics(res?.data?.data?.metrics);
    } catch (_) {}
  };

  useEffect(() => {
    fetchJobs();
    fetchSaved();
    fetchSummary();
  }, []);

  const companies = useMemo(
    () => Array.from(new Set(baseJobs.map((j) => j.companyName).filter(Boolean))).sort(),
    [baseJobs],
  );

  const newTodayCount = useMemo(
    () => baseJobs.filter((j) => moment(j.createdAt || j.startDate).isSame(moment(), "day")).length,
    [baseJobs],
  );

  const sourceJobs = showSavedOnly ? savedJobs : baseJobs;

  const filteredJobs = useMemo(() => {
    let list = [...sourceJobs];
    const q = keyword.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (j) => j.title?.toLowerCase().includes(q) || j.companyName?.toLowerCase().includes(q),
      );
    }

    if (drawerFilters.title.trim()) {
      const t = drawerFilters.title.trim().toLowerCase();
      list = list.filter((j) => j.title?.toLowerCase().includes(t));
    }
    if (drawerFilters.company) list = list.filter((j) => j.companyName === drawerFilters.company);
    if (drawerFilters.location.trim()) {
      const l = drawerFilters.location.trim().toLowerCase();
      list = list.filter((j) => j.location?.toLowerCase().includes(l));
    }
    if (drawerFilters.datePosted) {
      const daysMap = { "24h": 1, "3d": 3, "7d": 7, "14d": 14, "30d": 30 };
      const cutoff = moment().subtract(daysMap[drawerFilters.datePosted], "days");
      list = list.filter((j) => j.createdAt && moment(j.createdAt).isAfter(cutoff));
    }
    if (drawerFilters.skills.length) {
      list = list.filter((j) =>
        drawerFilters.skills.some((s) => j.skillsAndRequirements?.some((js) => js.toLowerCase() === s.toLowerCase())),
      );
    }
    if (drawerFilters.min) list = list.filter((j) => Number(j.budget ?? 0) >= Number(drawerFilters.min));
    if (drawerFilters.max) list = list.filter((j) => Number(j.budget ?? 0) <= Number(drawerFilters.max));
    if (drawerFilters.types.length) list = list.filter((j) => drawerFilters.types.includes(j.type));
    if (drawerFilters.modes.length) list = list.filter((j) => drawerFilters.modes.includes(j.workMode));
    if (drawerFilters.relocationOnly) list = list.filter((j) => j.workMode === "Relocation");

    return list.sort((a, b) => {
      if (sort === "salary-desc") return Number(b.budget ?? 0) - Number(a.budget ?? 0);
      if (sort === "salary-asc") return Number(a.budget ?? 0) - Number(b.budget ?? 0);
      return new Date(b.createdAt || b.startDate || 0) - new Date(a.createdAt || a.startDate || 0);
    });
  }, [sourceJobs, keyword, drawerFilters, sort]);

  useEffect(() => {
    setPage(1);
  }, [filteredJobs.length, showSavedOnly]);

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const pagedJobs = filteredJobs.slice((page - 1) * pageSize, page * pageSize);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (drawerFilters.title) chips.push({ key: "title", label: `Title: ${drawerFilters.title}`, clear: () => setDrawerFilters((p) => ({ ...p, title: "" })) });
    if (drawerFilters.company) chips.push({ key: "company", label: drawerFilters.company, clear: () => setDrawerFilters((p) => ({ ...p, company: "" })) });
    if (drawerFilters.location) chips.push({ key: "location", label: `Location: ${drawerFilters.location}`, clear: () => setDrawerFilters((p) => ({ ...p, location: "" })) });
    if (drawerFilters.datePosted)
      chips.push({
        key: "datePosted",
        label: DATE_POSTED_OPTIONS.find((o) => o.value === drawerFilters.datePosted)?.label,
        clear: () => setDrawerFilters((p) => ({ ...p, datePosted: "" })),
      });
    drawerFilters.skills.forEach((s) =>
      chips.push({ key: `skill-${s}`, label: s, clear: () => setDrawerFilters((p) => ({ ...p, skills: p.skills.filter((x) => x !== s) })) }),
    );
    if (drawerFilters.min) chips.push({ key: "min", label: `Min ₦${currencyFormat(Number(drawerFilters.min))}`, clear: () => setDrawerFilters((p) => ({ ...p, min: "" })) });
    if (drawerFilters.max) chips.push({ key: "max", label: `Max ₦${currencyFormat(Number(drawerFilters.max))}`, clear: () => setDrawerFilters((p) => ({ ...p, max: "" })) });
    drawerFilters.types.forEach((t) =>
      chips.push({ key: `type-${t}`, label: t, clear: () => setDrawerFilters((p) => ({ ...p, types: p.types.filter((x) => x !== t) })) }),
    );
    drawerFilters.modes.forEach((m) =>
      chips.push({ key: `mode-${m}`, label: m, clear: () => setDrawerFilters((p) => ({ ...p, modes: p.modes.filter((x) => x !== m) })) }),
    );
    if (drawerFilters.relocationOnly)
      chips.push({ key: "relocOnly", label: "Relocation provided", clear: () => setDrawerFilters((p) => ({ ...p, relocationOnly: false })) });
    return chips;
  }, [drawerFilters]);

  const toggleDrawerType = (t) =>
    setDrawerFilters((prev) => ({
      ...prev,
      types: prev.types.includes(t) ? prev.types.filter((x) => x !== t) : [...prev.types, t],
    }));

  const toggleDrawerMode = (m) =>
    setDrawerFilters((prev) => ({
      ...prev,
      modes: prev.modes.includes(m) ? prev.modes.filter((x) => x !== m) : [...prev.modes, m],
    }));

  const toggleDrawerSkill = (s) =>
    setDrawerFilters((prev) => ({
      ...prev,
      skills: prev.skills.includes(s) ? prev.skills.filter((x) => x !== s) : [...prev.skills, s],
    }));

  const resetDrawer = () => setDrawerFilters(emptyDrawerFilters);

  const applyDrawer = () => {
    setDrawerOpen(false);
    setPage(1);
  };

  const handleToggleStar = async (job) => {
    try {
      if (job?.isStarred) {
        await talentRemoveJobInteraction(job._id, "star");
        toastSuccess("Star removed successfully");
      } else {
        await talentJobInteraction({ jobId: job._id, interactionType: "star" });
        toastSuccess("Job starred successfully");
      }
      setBaseJobs((prev) => prev.map((j) => (j._id === job._id ? { ...j, isStarred: !j.isStarred } : j)));
      fetchSaved();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.pageHeader}>
        <div className={classes.pageTitleRow}>
          <div>
            <span className={classes.eyebrow}>
              <IconRss size={14} /> Job Feed
            </span>
            <h1 className={classes.pageTitle}>Find your next opportunity.</h1>
            <p className={classes.pageSubtitle}>
              Fresh roles matched to your profile — filter, save, and apply to the ones that fit.
            </p>
          </div>
          <button type="button" className={classes.btnAdvanced} onClick={() => setDrawerOpen(true)}>
            <IconAdjustmentsHorizontal size={16} /> Advanced Search
          </button>
        </div>
      </div>

      <div className={classes.dashStatShelf}>
        <div className={`${classes.dashStatCard} ${classes.statBlue}`}>
          <div className={classes.dashStatIcon}>
            <IconSparkles size={16} />
          </div>
          <div className={classes.dashStatValue}>{newTodayCount}</div>
          <div className={classes.dashStatLabel}>New Today</div>
        </div>
        <div className={`${classes.dashStatCard} ${classes.statGreen}`}>
          <div className={classes.dashStatIcon}>
            <IconBriefcase size={16} />
          </div>
          <div className={classes.dashStatValue}>{openRolesTotal}</div>
          <div className={classes.dashStatLabel}>Open Roles</div>
        </div>
        <div className={`${classes.dashStatCard} ${classes.statAmber}`}>
          <div className={classes.dashStatIcon}>
            <IconSend size={16} />
          </div>
          <div className={classes.dashStatValue}>{metrics?.jobsApplied ?? 0}</div>
          <div className={classes.dashStatLabel}>Applied</div>
        </div>
        <div className={`${classes.dashStatCard} ${classes.statIndigo}`}>
          <div className={classes.dashStatIcon}>
            <IconStar size={16} />
          </div>
          <div className={classes.dashStatValue}>{savedTotal}</div>
          <div className={classes.dashStatLabel}>Saved</div>
        </div>
      </div>

      <div className={classes.jfToolbar}>
        <div className={classes.jfSearchWrap}>
          <IconSearch size={16} />
          <input
            type="text"
            placeholder="Search job title or company"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <button
          type="button"
          className={`${classes.jfSavedToggle} ${showSavedOnly ? classes.jfSavedToggleActive : ""}`}
          aria-pressed={showSavedOnly}
          onClick={() => setShowSavedOnly((p) => !p)}
        >
          {showSavedOnly ? <IconStarFilled size={16} /> : <IconStar size={16} />} Saved{" "}
          <span className={classes.jfSavedCount}>{savedTotal}</span>
        </button>
        <div className={classes.jfSelectWrap}>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="salary-desc">Salary: High to Low</option>
            <option value="salary-asc">Salary: Low to High</option>
          </select>
          <IconChevronDown size={14} />
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className={classes.activeFilters}>
          <span className={classes.afLabel}>Active filters:</span>
          {activeFilters.map((f) => (
            <span key={f.key} className={classes.filterChip}>
              {f.label}
              <button onClick={f.clear} aria-label={`Remove ${f.label}`}>
                <IconX size={10} />
              </button>
            </span>
          ))}
          <button className={classes.afClear} onClick={resetDrawer}>
            Clear all
          </button>
        </div>
      )}

      <div className={classes.card}>
        <div className={classes.jfList}>
          {loading ? (
            <div className={classes.jfEmpty}>
              <div className={classes.jfEmptyTitle}>Loading…</div>
            </div>
          ) : pagedJobs.length === 0 ? (
            <div className={classes.jfEmpty}>
              <IconBriefcase size={32} />
              <div className={classes.jfEmptyTitle}>
                {showSavedOnly ? "No saved jobs yet" : "No jobs match your filters"}
              </div>
              <div className={classes.jfEmptyHint}>
                {showSavedOnly ? (
                  "Star a job from the feed to save it for later."
                ) : (
                  <>
                    Try a different keyword or{" "}
                    <span
                      className={classes.jfClearLink}
                      onClick={() => {
                        setKeyword("");
                        resetDrawer();
                      }}
                    >
                      clear filters
                    </span>
                    .
                  </>
                )}
              </div>
            </div>
          ) : (
            pagedJobs.map((job) => (
              <div key={job._id} className={classes.jfRow}>
                <div className={classes.jfRowMain}>
                  <div className={classes.jfIcon}>
                    <IconBriefcase size={20} />
                  </div>
                  <div className={classes.jfRowContent} onClick={() => setSelectedJob(job)}>
                    <div className={classes.jfTitleRow}>
                      <span className={classes.jfTitle}>{job.title}</span>
                    </div>
                    <div className={classes.jfMeta}>
                      {job.companyName && <span>{job.companyName}</span>}
                      {job.location && (
                        <>
                          <span className={classes.jfMetaDivider} />
                          <span>
                            <IconMapPin size={13} /> {job.location}
                          </span>
                        </>
                      )}
                      {job.type && (
                        <>
                          <span className={classes.jfMetaDivider} />
                          <span>
                            <IconBriefcase size={13} /> {job.type}
                          </span>
                        </>
                      )}
                      {job.workMode && (
                        <>
                          <span className={classes.jfMetaDivider} />
                          <span>
                            <IconWorld size={13} /> {job.workMode}
                          </span>
                        </>
                      )}
                      {job.duration && (
                        <>
                          <span className={classes.jfMetaDivider} />
                          <span>
                            <IconClock size={13} /> {job.duration}
                          </span>
                        </>
                      )}
                      <span className={classes.jfMetaDivider} />
                      <span>
                        <IconPlane size={13} /> {job.hasNoRelocationPlans === "Yes" ? "No relocation" : "Relocation provided"}
                      </span>
                    </div>
                    <div className={classes.jfDate}>
                      <IconCalendar size={13} /> Posted {formatPostedDate(job.createdAt || job.startDate)}
                    </div>
                  </div>
                  <div className={classes.jfRowSide}>
                    <span className={classes.jfSalary}>&#8358;{currencyFormat(Number(job.budget ?? 0))}</span>
                    <span className={classes.jfSideDivider} />
                    <div className={classes.jfActions}>
                      <button
                        className={`${classes.jfIconBtn} ${job.isStarred ? classes.jfIconBtnActive : ""}`}
                        title={job.isStarred ? "Remove star" : "Save job"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStar(job);
                        }}
                      >
                        {job.isStarred ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                      </button>
                      <button
                        className={classes.jfIconBtn}
                        title="Share this job"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareJob(job);
                        }}
                      >
                        <IconShare3 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className={classes.jfPagination}>
          <button className={classes.jfPageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`${classes.jfPageBtn} ${p === page ? classes.jfPageBtnActive : ""}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
          <button className={classes.jfPageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            ›
          </button>
        </div>
      )}

      {/* ── Filter drawer ── */}
      {drawerOpen && <div className={classes.filterOverlay} onClick={() => setDrawerOpen(false)} />}
      <aside className={`${classes.filterDrawer} ${drawerOpen ? classes.filterDrawerOpen : ""}`} aria-label="Advanced search filters">
        <div className={classes.fdHeader}>
          <div>
            <h2>Advanced Search</h2>
            <p>Filter the job feed to find your match.</p>
          </div>
          <button className={classes.fdClose} onClick={() => setDrawerOpen(false)} aria-label="Close filters">
            <IconX size={18} />
          </button>
        </div>
        <div className={classes.fdBody}>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Date Posted</label>
            <div className={classes.filterSelectWrap}>
              <select
                value={drawerFilters.datePosted}
                onChange={(e) => setDrawerFilters((p) => ({ ...p, datePosted: e.target.value }))}
              >
                {DATE_POSTED_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <IconChevronDown size={16} className={classes.selChevron} />
            </div>
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Job Title</label>
            <input
              type="text"
              className={classes.filterInput}
              placeholder="e.g. Frontend Developer"
              value={drawerFilters.title}
              onChange={(e) => setDrawerFilters((p) => ({ ...p, title: e.target.value }))}
            />
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Company</label>
            <div className={classes.filterSelectWrap}>
              <select
                value={drawerFilters.company}
                onChange={(e) => setDrawerFilters((p) => ({ ...p, company: e.target.value }))}
              >
                <option value="">Any company</option>
                {companies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <IconChevronDown size={16} className={classes.selChevron} />
            </div>
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Location</label>
            <input
              type="text"
              className={classes.filterInput}
              placeholder="City, Country"
              value={drawerFilters.location}
              onChange={(e) => setDrawerFilters((p) => ({ ...p, location: e.target.value }))}
            />
          </div>

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Skills</label>
            <div className={classes.multiWrap}>
              <div
                className={`${classes.multiTrigger} ${drawerFilters.skills.length ? classes.multiTriggerHasValue : ""}`}
                tabIndex={0}
                onClick={() => setSkillsPanelOpen((p) => !p)}
              >
                {drawerFilters.skills.length === 0 ? (
                  <span>Select skills</span>
                ) : (
                  drawerFilters.skills.map((s) => (
                    <span key={s} className={classes.multiChip} onClick={(e) => e.stopPropagation()}>
                      {s}
                      <span
                        className={classes.rm}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDrawerSkill(s);
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))
                )}
                <IconChevronDown size={16} className={classes.selChevron} />
              </div>
              {skillsPanelOpen && (
                <div className={classes.multiPanel}>
                  {ALL_SKILLS.slice(0, 60).map((s) => (
                    <div
                      key={s}
                      className={`${classes.multiOption} ${drawerFilters.skills.includes(s) ? classes.multiOptionSelected : ""}`}
                      onClick={() => toggleDrawerSkill(s)}
                    >
                      <span className={classes.optBox}>{drawerFilters.skills.includes(s) && <IconCheck size={12} />}</span>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Salary Range (₦)</label>
            <div className={classes.payRow}>
              <div>
                <label>Min</label>
                <input
                  type="number"
                  className={classes.filterInput}
                  placeholder="0"
                  min="0"
                  value={drawerFilters.min}
                  onChange={(e) => setDrawerFilters((p) => ({ ...p, min: e.target.value }))}
                />
              </div>
              <div>
                <label>Max</label>
                <input
                  type="number"
                  className={classes.filterInput}
                  placeholder="Any"
                  min="0"
                  value={drawerFilters.max}
                  onChange={(e) => setDrawerFilters((p) => ({ ...p, max: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Employment Type</label>
            <div className={classes.checkStack}>
              {EMPLOYMENT_TYPES.map((t) => (
                <label
                  key={t}
                  className={`${classes.checkRow} ${drawerFilters.types.includes(t) ? classes.checkRowChecked : ""}`}
                  onClick={() => toggleDrawerType(t)}
                >
                  <span className={classes.checkBox}>{drawerFilters.types.includes(t) && <IconCheck size={13} />}</span> {t}
                </label>
              ))}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Work Mode</label>
            <div className={classes.checkStack}>
              {WORK_MODES.map((m) => (
                <label
                  key={m}
                  className={`${classes.checkRow} ${drawerFilters.modes.includes(m) ? classes.checkRowChecked : ""}`}
                  onClick={() => toggleDrawerMode(m)}
                >
                  <span className={classes.checkBox}>{drawerFilters.modes.includes(m) && <IconCheck size={13} />}</span> {m}
                </label>
              ))}
            </div>
          </div>

          <div className={classes.filterDivider} />

          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Relocation</label>
            <div className={classes.checkStack}>
              <label
                className={`${classes.checkRow} ${drawerFilters.relocationOnly ? classes.checkRowChecked : ""}`}
                onClick={() => setDrawerFilters((p) => ({ ...p, relocationOnly: !p.relocationOnly }))}
              >
                <span className={classes.checkBox}>{drawerFilters.relocationOnly && <IconCheck size={13} />}</span> Relocation
                provided only
              </label>
            </div>
          </div>
        </div>
        <div className={classes.fdFooter}>
          <button className={classes.btnOutline} onClick={resetDrawer}>
            <IconRotate size={15} /> Reset
          </button>
          <button className={classes.btnUpdate} onClick={applyDrawer}>
            <IconFilter size={15} /> Show {filteredJobs.length} result{filteredJobs.length === 1 ? "" : "s"}
          </button>
        </div>
      </aside>

      {/* ── Job detail modal ── */}
      {selectedJob && (
        <div className={classes.modalBackdrop} onClick={() => setSelectedJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={classes.modalHeadIcon}>
                <IconBriefcase size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>{selectedJob.title}</div>
                <div className={classes.modalHeadSub}>
                  {selectedJob.companyName} <span>·</span> Posted {formatPostedDate(selectedJob.createdAt || selectedJob.startDate)}
                </div>
              </div>
              <button className={classes.modalClose} onClick={() => setSelectedJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.detailStats}>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>{selectedJob.applicantTalentIds?.length ?? 0}</div>
                  <div className={classes.detailStatLbl}>Applicants</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>{selectedJob.duration || "—"}</div>
                  <div className={classes.detailStatLbl}>Duration</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>
                    {selectedJob.endDate ? moment(selectedJob.endDate).format("DD MMM") : "—"}
                  </div>
                  <div className={classes.detailStatLbl}>Deadline</div>
                </div>
              </div>

              <div className={classes.detailGrid}>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Salary</div>
                  <div className={classes.detailCellVal}>&#8358;{currencyFormat(Number(selectedJob.budget ?? 0))}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Job Type</div>
                  <div className={classes.detailCellVal}>{selectedJob.type || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Work Mode</div>
                  <div className={classes.detailCellVal}>{selectedJob.workMode || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Location</div>
                  <div className={classes.detailCellVal}>{selectedJob.location || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Relocation</div>
                  <div className={classes.detailCellVal}>
                    {selectedJob.hasNoRelocationPlans === "Yes" ? "Not provided" : "Provided"}
                  </div>
                </div>
              </div>

              {selectedJob.description && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>About this role</div>
                  <div className={classes.detailDesc}>{selectedJob.description}</div>
                </div>
              )}

              {selectedJob.acceptanceCriteria && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Requirements</div>
                  <ul className={classes.detailReqList}>
                    {selectedJob.acceptanceCriteria
                      .split("\n")
                      .filter(Boolean)
                      .map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                  </ul>
                </div>
              )}

              {selectedJob.skillsAndRequirements?.length > 0 && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Skills needed</div>
                  <div className={classes.detailSkills}>
                    {selectedJob.skillsAndRequirements.map((s, i) => (
                      <span key={i} className={classes.detailSkill}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.modalFoot}>
              <button className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={() => setSelectedJob(null)}>
                <IconX size={14} /> Close
              </button>
              <button className={`${classes.modalBtn} ${classes.modalBtnStar}`} onClick={() => handleToggleStar(selectedJob)}>
                {selectedJob.isStarred ? <IconStarFilled size={14} /> : <IconStar size={14} />}{" "}
                {selectedJob.isStarred ? "Saved" : "Save"}
              </button>
              <button className={`${classes.modalBtn} ${classes.modalBtnPrimary}`} onClick={() => navigate(`/job/${selectedJob._id}`)}>
                <IconSend size={14} /> View &amp; Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share modal ── */}
      {shareJob && (
        <div className={classes.modalBackdrop} onClick={() => setShareJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconBlue}`}>
                <IconShare3 size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Share this job</div>
                <div className={classes.modalHeadSub}>{shareJob.title}</div>
              </div>
              <button className={classes.modalClose} onClick={() => setShareJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.shareSocialGrid}>
                {[
                  {
                    Icon: IconBrandWhatsapp,
                    bg: "#25D366",
                    label: "WhatsApp",
                    onClick: () =>
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(`Check out this job: ${shareJob.title} at ${shareJob.companyName} ${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandTwitter,
                    bg: "#0d1b3e",
                    label: "X",
                    onClick: () =>
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this job: ${shareJob.title} at ${shareJob.companyName}`)}&url=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandFacebook,
                    bg: "#1877f2",
                    label: "Facebook",
                    onClick: () =>
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                ].map(({ Icon, bg, label, onClick }) => (
                  <button key={label} className={classes.shareSocialBtn} onClick={onClick}>
                    <span className={classes.shareSocialIcon} style={{ background: bg }}>
                      <Icon size={19} />
                    </span>
                    {label}
                  </button>
                ))}
              </div>
              <span className={classes.shareCopyLabel}>Or copy link</span>
              <div className={classes.shareCopyRow}>
                <input type="text" readOnly value={`${window.location.origin}/job/${shareJob._id}`} />
                <button
                  className={`${classes.modalBtn} ${classes.modalBtnPrimary} ${classes.shareCopyBtn}`}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/job/${shareJob._id}`);
                      toastSuccess("Job link copied to clipboard!");
                      setShareJob(null);
                    } catch {
                      errorHandler("Failed to copy link", "error");
                    }
                  }}
                >
                  <IconCopy size={14} /> Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobFeed;
